/* eslint-disable dot-notation */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Spacings } from '@styles/index';
import { useRouter } from 'next/router';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { evtNameCategoryMap } from '@constants/index';
import { SYLLABUS_CLICKED_KEYS } from '@constants/segment';
import { segment } from '@actions/analytics';
import { PLAYER_HOTKEYS } from '@constants/unacademyIcons/icons.constants';
import Header from './header';
import ImageViewer from './ImageViewer';
import EpubContainer from './EpubContainer';
import { EpubContextProvider } from './Context/EpubContext';
import {
  EPUB_NOTES,
  NOTES_HIDE_NAVIGATION_CLICKED,
  NOTES_IMAGE_CLICKED,
  NOTES_TOPIC_DROPDOWN_SELECTED
} from './utils';
import SideNav from './sideNav';
import PDFContainer from './PDFContainer';

interface CSSProps {
  showNavigation: boolean;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: calc(100vh - 28px);
  grid-column: 1/-1;
  position: relative;
  user-select: none;
`;

const InnerWrapper = styled.div<CSSProps>`
  height: 100%;
  display: grid;
  margin-top: ${Spacings.SPACING_3B};
  grid-template-columns: max-content auto;
`;

const EpubNotes = ({ selectedConcept: initialSelectedConcept }) => {
  const { query = {} } = useRouter();
  const { topicUID, goalUID } = query;

  const [image, setImage] = useState(null);
  const [currentNotesUID, setCurrentNotesUID] = useState('');
  const [showNavigation, setShowNavigation] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(topicUID as string);
  const [resize, setResize] = useState(true);
  const [showPDFViewer, setShowPDFViewer] = useState(false);
  const [conceptPDFInfo, setConceptPDFInfo] = useState({});
  const [selectedSubItemIds, setSelectedSubItemIds] = useState({});

  const goalSegmentData = useGoalEventProps({ goalUID: goalUID as string });

  useEffect(() => {
    if (
      initialSelectedConcept &&
      !initialSelectedConcept.isEpubAvailable &&
      initialSelectedConcept.isNotesAvailable &&
      !showPDFViewer
    ) {
      setShowPDFViewer(true);
    } else {
      setShowPDFViewer(false);
    }
  }, [initialSelectedConcept]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showNavigation]);

  const onImageClick = (src, height, width) => {
    segment.track(
      NOTES_IMAGE_CLICKED,
      {
        ...goalSegmentData,
        last_primary_source: EPUB_NOTES,
        last_primary_source_section: EPUB_NOTES,
        notes_uid: currentNotesUID,
        notes_topic: topicUID
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      SYLLABUS_CLICKED_KEYS
    );
    setImage({
      src,
      height,
      width
    });
  };
  const onImageViewerClose = () => setImage(null);
  const onNavigationClick = () => {
    segment.track(
      NOTES_HIDE_NAVIGATION_CLICKED,
      {
        ...goalSegmentData,
        last_primary_source: EPUB_NOTES,
        last_primary_source_section: EPUB_NOTES,
        notes_topic: selectedTopic,
        notes_uid: currentNotesUID,
        nav_status: showNavigation ? 'collapsed' : 'open'
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      SYLLABUS_CLICKED_KEYS
    );
    setShowNavigation(!showNavigation);
    setTimeout(() => {
      setResize(!resize);
    }, 300);
  };
  const onChangeTopic = (topic) => {
    segment.track(
      NOTES_TOPIC_DROPDOWN_SELECTED,
      {
        ...goalSegmentData,
        last_primary_source: EPUB_NOTES,
        last_primary_source_section: EPUB_NOTES,
        notes_topic: topic.name,
        notes_uid: currentNotesUID
      },
      evtNameCategoryMap.WITHOUT_CATEGORY,
      true,
      SYLLABUS_CLICKED_KEYS
    );
    setSelectedTopic(topic);
  };

  const toggleFullScreen = () => {
    if (
      !(
        document.fullscreenElement ||
        document['mozFullScreenElement'] ||
        document['msFullscreenElement'] ||
        document['webkitFullscreenElement']
      )
    ) {
      document.documentElement.requestFullscreen();
    } else if (
      document.exitFullscreen ||
      document['webkitExitFullscreen'] ||
      document['mozCancelFullScreen'] ||
      document['msExitFullscreen']
    ) {
      document.exitFullscreen();
    }
  };

  const handleKeyDown = (event) => {
    if (event.target.name === 'Epub-Search') return;

    // N is pressed
    if (event.which === 78) {
      onNavigationClick();
    }

    if (
      event.which === PLAYER_HOTKEYS.F ||
      event.which === PLAYER_HOTKEYS.ESC
    ) {
      toggleFullScreen();
    }
  };
  if (!initialSelectedConcept) return null;
  return (
    <EpubContextProvider
      currentNotesUID={currentNotesUID}
      initialSelectedConcept={initialSelectedConcept}
    >
      <Wrapper>
        <Header
          showNavigation={showNavigation}
          onNavigationClick={onNavigationClick}
          showPDFViewer={showPDFViewer}
        />
        <InnerWrapper showNavigation={showNavigation}>
          <SideNav
            showNavigation={showNavigation}
            topicUID={selectedTopic}
            onChangeTopic={onChangeTopic}
            setShowPDFViewer={setShowPDFViewer}
            conceptPDFInfo={conceptPDFInfo}
            selectedSubItemIds={selectedSubItemIds}
            setSelectedSubItemIds={setSelectedSubItemIds}
          />
          {showPDFViewer ? (
            <PDFContainer
              setCurrentNotesUID={setCurrentNotesUID}
              setConceptPDFInfo={setConceptPDFInfo}
              conceptPDFInfo={conceptPDFInfo}
              selectedSubItemIds={selectedSubItemIds}
              setSelectedSubItemIds={setSelectedSubItemIds}
            />
          ) : (
            <EpubContainer
              resize={resize}
              onImageClick={onImageClick}
              setCurrentNotesUID={setCurrentNotesUID}
            />
          )}
        </InnerWrapper>
        <ImageViewer
          imageSrc={image?.src}
          imageHeight={image?.height}
          imageWidth={image?.width}
          open={image && !!image?.src}
          onClose={onImageViewerClose}
        />
      </Wrapper>
    </EpubContextProvider>
  );
};

export default EpubNotes;
